if (typeof ProductImageZoom !== 'function') {
  class ProductImageZoom extends HTMLElement {
    constructor() {
      super();

      this.zoom = this._ensureViewer();
      this.setAttribute('role', 'button');
      this.setAttribute('aria-label', 'Open product image viewer');
      this.removeAttribute('aria-hidden');
      this.setAttribute('tabindex', '0');

      this.addEventListener('click', event => {
        event.preventDefault();
        this._open();
      });

      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._open();
        }
      });
    }

    _ensureViewer() {
      let viewer = document.getElementById('zoom');

      if (!viewer) {
        viewer = document.createElement('div');
        viewer.id = 'zoom';
        document.body.append(viewer);
      }

      if (viewer.dataset.labooGalleryViewer !== 'true') {
        viewer.dataset.labooGalleryViewer = 'true';
        viewer.classList.add('laboo-product-lightbox');
        viewer.setAttribute('role', 'dialog');
        viewer.setAttribute('aria-modal', 'true');
        viewer.setAttribute('aria-label', 'Product image viewer');
        viewer.setAttribute('aria-hidden', 'true');
        viewer.innerHTML = `
          <div class="laboo-product-lightbox__stage">
            <img alt="" width="1" height="1" decoding="async" draggable="false" />
            <video class="laboo-product-lightbox__video" muted loop playsinline preload="none" width="1080" height="1080" disablepictureinpicture disableremoteplayback hidden></video>
            <button type="button" class="laboo-product-lightbox__video-play" aria-label="Play room video" hidden><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg></button>
          </div>
          <button type="button" class="zoom__exit laboo-product-lightbox__close" aria-label="Close image viewer">
            ${KROWN.settings.symbols.close}
          </button>
          <button type="button" class="laboo-product-lightbox__nav laboo-product-lightbox__prev" aria-label="Previous image">&#8249;</button>
          <button type="button" class="laboo-product-lightbox__nav laboo-product-lightbox__next" aria-label="Next image">&#8250;</button>
          <span class="zoom__loader">${KROWN.settings.symbols.zoom_loader}</span>
          <p class="laboo-product-lightbox__count" aria-live="polite"></p>
          <div class="laboo-product-lightbox__footer">
            <div class="laboo-product-lightbox__thumbnails" aria-label="Product images"></div>
            <div class="laboo-product-lightbox__progress" aria-hidden="true"><span></span></div>
          </div>
          <div class="zoom__overlay"></div>
        `;

        viewer.querySelector('.laboo-product-lightbox__close').addEventListener('click', () => {
          viewer._labooActiveController?._close();
        });
        viewer.querySelector('.laboo-product-lightbox__prev').addEventListener('click', () => {
          viewer._labooActiveController?._showRelative(-1);
        });
        viewer.querySelector('.laboo-product-lightbox__next').addEventListener('click', () => {
          viewer._labooActiveController?._showRelative(1);
        });
        // Laboo 13 Oct 2026: room video item (play button for reduced motion or blocked autoplay)
        const labooVideo = viewer.querySelector('.laboo-product-lightbox__video');
        const labooVideoPlay = viewer.querySelector('.laboo-product-lightbox__video-play');
        labooVideoPlay.addEventListener('click', () => {
          labooVideo.muted = true;
          labooVideoPlay.hidden = true;
          const playing = labooVideo.play();
          if (playing && typeof playing.catch === 'function') playing.catch(() => { labooVideoPlay.hidden = false; });
        });
        labooVideo.addEventListener('click', () => {
          if (labooVideo.paused) return;
          labooVideo.pause();
          labooVideoPlay.hidden = false;
        });

        viewer.querySelector('.zoom__overlay').addEventListener('click', () => {
          viewer._labooActiveController?._close();
        });

        // Laboo 4 Oct 2026: swipe left/right between images on touch screens
        let labooSwipeX = null, labooSwipeY = null;
        viewer.addEventListener('touchstart', event => {
          if (event.touches.length !== 1) { labooSwipeX = null; return; }
          labooSwipeX = event.touches[0].clientX; labooSwipeY = event.touches[0].clientY;
        }, { passive: true });
        viewer.addEventListener('touchend', event => {
          if (labooSwipeX === null || !event.changedTouches.length) return;
          const dx = event.changedTouches[0].clientX - labooSwipeX;
          const dy = event.changedTouches[0].clientY - labooSwipeY;
          labooSwipeX = null;
          if (viewer.classList.contains('is-zoomed')) return;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            viewer._labooActiveController?._showRelative(dx < 0 ? 1 : -1);
          }
        }, { passive: true });

        // Laboo 18 Oct 2026: zoom the full-size image. Desktop: click to zoom, the image follows the mouse.
        // Touch: double-tap to zoom, drag to pan, pinch to zoom. Never on the room video.
        const zoomImage = viewer.querySelector('.laboo-product-lightbox__stage img');
        const zoomStage = zoomImage.parentElement;
        const zoomClamp = (value, min, max) => Math.min(Math.max(value, min), max);
        let zoomState = null, zoomHinted = false;
        const zoomMax = () => {
          if (viewer.classList.contains('is-video') || zoomImage.hidden || !zoomImage.naturalWidth || !zoomImage.offsetWidth) return 1;
          return Math.min(2.5, zoomImage.naturalWidth / zoomImage.offsetWidth);
        };
        // Visible area (the whole viewer) in stage coordinates
        const zoomArea = () => {
          const stageRect = zoomStage.getBoundingClientRect();
          const viewerRect = viewer.getBoundingClientRect();
          return { x0: viewerRect.left - stageRect.left, y0: viewerRect.top - stageRect.top, w: viewerRect.width, h: viewerRect.height, stageRect };
        };
        const zoomSet = (scale, left, top) => {
          const area = zoomArea();
          const w = zoomImage.offsetWidth, h = zoomImage.offsetHeight;
          const fit = (pos, size, origin, start, length) => size <= length
            ? zoomClamp(origin + (size / scale - size) / 2, start, start + length - size)
            : zoomClamp(pos, start + length - size, start);
          left = fit(left, w * scale, zoomImage.offsetLeft, area.x0, area.w);
          top = fit(top, h * scale, zoomImage.offsetTop, area.y0, area.h);
          zoomState = { scale, left, top };
          zoomImage.style.setProperty('--laboo-lb-zoom', `translate(${left - zoomImage.offsetLeft}px, ${top - zoomImage.offsetTop}px) scale(${scale})`);
          viewer.classList.add('is-zoomed');
          zoomImage.title = '';
        };
        const zoomReset = () => {
          zoomState = null;
          zoomImage.style.removeProperty('--laboo-lb-zoom');
          viewer.classList.remove('is-zoomed', 'is-panning');
        };
        // Desktop: map the cursor across the viewer to the whole zoomed image
        const zoomFollow = (clientX, clientY, scale) => {
          const area = zoomArea();
          const fx = zoomClamp((clientX - area.stageRect.left - area.x0 - area.w * 0.1) / (area.w * 0.8), 0, 1);
          const fy = zoomClamp((clientY - area.stageRect.top - area.y0 - area.h * 0.1) / (area.h * 0.8), 0, 1);
          zoomSet(scale, area.x0 - fx * (zoomImage.offsetWidth * scale - area.w), area.y0 - fy * (zoomImage.offsetHeight * scale - area.h));
        };
        viewer._labooZoomReset = zoomReset;
        viewer._labooZoomReady = () => {
          const zoomable = zoomMax() > 1.05;
          viewer.classList.toggle('is-zoomable', zoomable);
          zoomImage.title = zoomable && !zoomHinted && window.matchMedia('(pointer: fine)').matches ? 'Click to zoom' : '';
        };

        zoomImage.addEventListener('load', () => viewer._labooZoomReady());
        zoomImage.addEventListener('click', event => {
          if (Date.now() - (viewer._labooTouchAt || 0) < 800) return;
          const scale = zoomMax();
          if (zoomState) zoomReset();
          else if (scale > 1.05) { zoomHinted = true; zoomFollow(event.clientX, event.clientY, scale); }
          else viewer._labooActiveController?._close();
        });
        let zoomFrame = 0;
        viewer.addEventListener('mousemove', event => {
          if (!zoomState || zoomFrame || Date.now() - (viewer._labooTouchAt || 0) < 800) return;
          zoomFrame = requestAnimationFrame(() => {
            zoomFrame = 0;
            if (zoomState) zoomFollow(event.clientX, event.clientY, zoomState.scale);
          });
        });
        window.addEventListener('resize', () => { if (zoomState) zoomReset(); });

        let zoomTouch = null, zoomTap = null, zoomLastTap = null;
        const touchPoint = (touches, rect) => ({
          x: (touches[0].clientX + touches[touches.length - 1].clientX) / 2 - rect.left,
          y: (touches[0].clientY + touches[touches.length - 1].clientY) / 2 - rect.top,
          d: touches.length > 1 ? Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY) : 0
        });
        zoomImage.addEventListener('touchstart', event => {
          const rect = zoomStage.getBoundingClientRect();
          const point = touchPoint(event.touches, rect);
          zoomTap = event.touches.length === 1 ? { x: point.x, y: point.y, time: Date.now() } : null;
          const state = zoomState || { scale: 1, left: zoomImage.offsetLeft, top: zoomImage.offsetTop };
          if (event.touches.length === 2 && zoomMax() > 1.05) zoomTouch = { ...state, ...point };
          else if (event.touches.length === 1 && zoomState) zoomTouch = { ...state, ...point };
          else zoomTouch = null;
          if (zoomTouch) viewer.classList.add('is-panning');
        }, { passive: true });
        zoomImage.addEventListener('touchmove', event => {
          if (!zoomTouch) return;
          event.preventDefault();
          const point = touchPoint(event.touches, zoomStage.getBoundingClientRect());
          if (zoomTouch.d && point.d) {
            const scale = zoomClamp(zoomTouch.scale * point.d / zoomTouch.d, 1, zoomMax());
            const ratio = scale / zoomTouch.scale;
            zoomSet(scale, point.x - (zoomTouch.x - zoomTouch.left) * ratio, point.y - (zoomTouch.y - zoomTouch.top) * ratio);
          } else if (!zoomTouch.d) {
            zoomSet(zoomTouch.scale, zoomTouch.left + point.x - zoomTouch.x, zoomTouch.top + point.y - zoomTouch.y);
          }
        }, { passive: false });
        zoomImage.addEventListener('touchend', event => {
          viewer._labooTouchAt = Date.now();
          if (event.touches.length) return;
          zoomTouch = null;
          viewer.classList.remove('is-panning');
          if (zoomState && zoomState.scale < 1.05) zoomReset();
          const touch = event.changedTouches[0];
          const rect = zoomStage.getBoundingClientRect();
          const x = touch.clientX - rect.left, y = touch.clientY - rect.top;
          if (!zoomTap || Math.hypot(x - zoomTap.x, y - zoomTap.y) > 12 || Date.now() - zoomTap.time > 300) { zoomLastTap = null; return; }
          if (zoomLastTap && Date.now() - zoomLastTap.time < 320 && Math.hypot(x - zoomLastTap.x, y - zoomLastTap.y) < 40) {
            zoomLastTap = null;
            const scale = zoomMax();
            if (zoomState) zoomReset();
            else if (scale > 1.05) zoomSet(scale, x - (x - zoomImage.offsetLeft) * scale, y - (y - zoomImage.offsetTop) * scale);
          } else {
            zoomLastTap = { x, y, time: Date.now() };
          }
        });

        document.addEventListener('keydown', event => {
          if (!viewer.classList.contains('opened')) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            if (viewer.classList.contains('is-zoomed')) zoomReset();
            else viewer._labooActiveController?._close();
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            viewer._labooActiveController?._showRelative(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            viewer._labooActiveController?._showRelative(1);
          }
        });
      }

      return viewer;
    }

    _getImages() {
      const productPage = this.closest('product-page') || document;
      return Array.from(productPage.querySelectorAll('product-image-zoom.product-gallery-item__zoom, .product-gallery-item[data-laboo-room-video]'))
        .filter(item => this._isVideoItem(item) || item.getAttribute('data-image'));
    }

    // Laboo 13 Oct 2026: the room video slide (snippets/laboo-room-video.liquid) is a viewer item too
    _isVideoItem(item) {
      return !!item && item.hasAttribute('data-laboo-room-video');
    }

    _videoSource(item) {
      const video = item.querySelector('video');
      const source = item.querySelector('video source');
      return {
        src: (source && (source.getAttribute('src') || source.getAttribute('data-src'))) || (video && video.getAttribute('src')) || '',
        poster: (video && video.getAttribute('poster')) || '',
        label: (video && video.getAttribute('aria-label')) || 'Room video'
      };
    }

    _open() {

      this.items = this._getImages();
      this.index = Math.max(this.items.indexOf(this), 0);
      this.zoom._labooActiveController = this;
      this.lastTrigger = this;

      document.documentElement.classList.add('laboo-product-lightbox-open');
      document.body.classList.add('laboo-product-lightbox-open');
      this.zoom.classList.add('opened');
      this.zoom.setAttribute('aria-hidden', 'false');
      this._renderThumbnails();
      this._showCurrent();
      this.zoom.querySelector('.laboo-product-lightbox__close').focus({ preventScroll: true });
    }

    _renderThumbnails() {
      const thumbnailRail = this.zoom.querySelector('.laboo-product-lightbox__thumbnails');
      const footer = this.zoom.querySelector('.laboo-product-lightbox__footer');
      const fragment = document.createDocumentFragment();

      this.items.forEach((item, index) => {
        const isVideo = this._isVideoItem(item);
        const sourceImage = isVideo ? null : item.closest('.product-gallery-item')?.querySelector('img');
        const button = document.createElement('button');
        const thumbnail = document.createElement('img');
        let thumbnailUrl = isVideo ? this._videoSource(item).poster : item.getAttribute('data-image');

        if (!isVideo) try {
          const url = new URL(thumbnailUrl, window.location.origin);
          url.searchParams.set('width', '160');
          thumbnailUrl = url.toString();
        } catch (error) {
          // Keep the original image URL if it cannot be parsed.
        }

        button.type = 'button';
        button.className = 'laboo-product-lightbox__thumbnail';
        button.dataset.lightboxIndex = index;
        button.setAttribute('aria-label', `View image ${index + 1} of ${this.items.length}`);
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this._showIndex(index));

        thumbnail.src = thumbnailUrl;
        thumbnail.alt = '';
        const sourceWidth = Number(sourceImage?.getAttribute('width')) || 160;
        const sourceHeight = Number(sourceImage?.getAttribute('height')) || 160;
        thumbnail.width = 160;
        thumbnail.height = Math.max(1, Math.round(160 * sourceHeight / sourceWidth));
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        button.title = isVideo ? this._videoSource(item).label : (sourceImage?.alt || `Image ${index + 1}`);
        if (isVideo) button.classList.add('is-video');
        button.append(thumbnail);
        fragment.append(button);
      });

      thumbnailRail.replaceChildren(fragment);
      footer.hidden = this.items.length < 2;
    }

    _showCurrent() {
      const item = this.items[this.index];
      const image = this.zoom.querySelector('.laboo-product-lightbox__stage img');
      const isVideo = this._isVideoItem(item);
      const sourceImage = isVideo ? null : item.closest('.product-gallery-item')?.querySelector('img');
      const previousButton = this.zoom.querySelector('.laboo-product-lightbox__prev');
      const nextButton = this.zoom.querySelector('.laboo-product-lightbox__next');
      const counter = this.zoom.querySelector('.laboo-product-lightbox__count');
      const hasMultipleImages = this.items.length > 1;

      this.zoom._labooZoomReset?.();
      this.zoom.classList.remove('loaded', 'is-zoomable');
      this.zoom.classList.toggle('is-video', isVideo);
      image.hidden = isVideo;
      this._syncVideo(isVideo ? item : null);

      if (isVideo) {
        image.onload = null;
        image.onerror = null;
        this.zoom.classList.add('loaded');
      } else {
      image.alt = sourceImage?.alt || 'Product image';
      image.width = Number(sourceImage?.getAttribute('width')) || 1;
      image.height = Number(sourceImage?.getAttribute('height')) || 1;
      image.onload = () => this.zoom.classList.add('loaded');
      image.onerror = () => this.zoom.classList.add('loaded');
      image.src = item.getAttribute('data-image');

      if (image.complete) {
        this.zoom.classList.add('loaded');
        this.zoom._labooZoomReady?.();
      }
      }

      previousButton.hidden = !hasMultipleImages;
      nextButton.hidden = !hasMultipleImages;
      counter.hidden = !hasMultipleImages;
      counter.textContent = `${this.index + 1} / ${this.items.length}`;
      this._updateThumbnails();
    }

    _syncVideo(item) {
      const video = this.zoom.querySelector('.laboo-product-lightbox__video');
      const playButton = this.zoom.querySelector('.laboo-product-lightbox__video-play');
      if (!video) return;

      if (!item) {
        if (!video.paused) video.pause();
        video.hidden = true;
        if (playButton) playButton.hidden = true;
        return;
      }

      const data = this._videoSource(item);
      if (data.poster && video.getAttribute('poster') !== data.poster) video.setAttribute('poster', data.poster);
      if (data.src && video.getAttribute('src') !== data.src) video.setAttribute('src', data.src);
      video.setAttribute('aria-label', data.label);
      video.muted = true;
      video.hidden = false;

      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        if (playButton) playButton.hidden = !video.paused;
        return;
      }
      if (playButton) playButton.hidden = true;
      const playing = video.play();
      if (playing && typeof playing.catch === 'function') {
        playing.catch(() => { if (playButton) playButton.hidden = false; });
      }
    }

    _updateThumbnails() {
      const buttons = Array.from(this.zoom.querySelectorAll('.laboo-product-lightbox__thumbnail'));
      const selectedButton = buttons[this.index];
      const progressThumb = this.zoom.querySelector('.laboo-product-lightbox__progress span');
      const progressWidth = Math.max(100 / Math.max(this.items.length, 1), 18);
      const progressPosition = this.items.length > 1
        ? (100 - progressWidth) * (this.index / (this.items.length - 1))
        : 0;

      buttons.forEach((button, index) => {
        const selected = index === this.index;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      progressThumb.style.width = `${progressWidth}%`;
      progressThumb.style.left = `${progressPosition}%`;
      selectedButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    _showIndex(index) {
      if (!this.items?.length || !Number.isInteger(index)) return;
      this.index = Math.min(Math.max(index, 0), this.items.length - 1);
      this._showCurrent();
    }

    _showRelative(direction) {
      if (!this.items?.length) return;
      this._showIndex((this.index + direction + this.items.length) % this.items.length);
    }

    _close() {
      if (!this.zoom.classList.contains('opened')) return;

      this._syncVideo(null);
      this.zoom._labooZoomReset?.();
      this.zoom.classList.remove('opened', 'loaded', 'is-video', 'is-zoomable');
      this.zoom.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('laboo-product-lightbox-open');
      document.body.classList.remove('laboo-product-lightbox-open');

      window.setTimeout(() => {
        const image = this.zoom.querySelector('.laboo-product-lightbox__stage img');
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = false;
      }, 220);

      this.lastTrigger?.focus({ preventScroll: true });
    }
  }

  if (typeof customElements.get('product-image-zoom') === 'undefined') {
    customElements.define('product-image-zoom', ProductImageZoom);
  }
}
